import React, { useState, useEffect, useRef } from "react";
import { Grid, Snackbar, Alert, CircularProgress, InputAdornment, InputLabel } from "@mui/material";
import { useBatteryConfig } from "../context/BatteryConfigContext";
import { useNavigate } from "react-router-dom";
import { AiOutlineDownload } from "react-icons/ai";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BatteryDefaultImage from "../../../assets/cellCircuit.png";
import {
  StyledBox,
  StyledTypography,
  StyledFormControl,
  StyledButton,
  StyledMenuItem,
  StyledInput,
  StyledTextField,
  StyledSelect,
  StyledTypographyBlack
} from "../styles/styles";
import SampleDataBatteryPack from "../data/SampleDataBatteryPack.csv";

const allowedImageTypes = ["image/jpeg", "image/png"];
const allowedCsvTypes = ["text/csv"];


const validateCSVData = (csvFile) => {
  return new Promise((resolve, reject) => {
  const Headers = `Time [s],Velocity [km/h],Elevation [m],Throttle [%],Motor Torque [Nm],Longitudinal Acceleration [m/s^2],Regenerative Braking Signal,Battery Voltage [V],Battery Current [A],Battery Temperature [蚓],max. Battery Temperature [蚓],SoC [%],displayed SoC [%],min. SoC [%],max. SoC [%),Heating Power CAN [kW],Heating Power LIN [W],Requested Heating Power [W],AirCon Power [kW],Heater Signal,Heater Voltage [V],Heater Current [A],Ambient Temperature [蚓],Coolant Temperature Heatercore [蚓],Requested Coolant Temperature [蚓],Coolant Temperature Inlet [蚓],Heat Exchanger Temperature [蚓],Cabin Temperature Sensor [蚓],Module Voltage 1 [V],Module Voltage 2 [V],Module Voltage 3 [V],Module_1_Temp,Module_2_Temp,Module_3_Temp`;
  const reader = new FileReader();
  reader.onload = (event) => {
    const text = event.target.result;
    const rows = text.split("\n");
    if(rows[0] !== Headers){
      reject(new Error("Invalid CSV file. Please upload a valid CSV file."));
    }
    if(rows.length < 2){
      reject(new Error("Invalid CSV file. Please upload a valid CSV file."));
    }
    resolve(true);
  }
    reader.readAsText(csvFile);
  });
}

const SubmitBatteryData = () => {
  const [formData, setFormData] = useState({
    batteryPics: null,
    voltage: 30,
    capacity: 10,
    capacityUnit: "Ah",
    ageYears: 0,
    ageMonths: 0,
    chemistry: "lpf",
    format: "cylindrical",
    makeModel: "Samsung 18650",
    uploadData: null,
    cellVoltage: 3.7,
    cellCapacity: 5,
  });
  const [batteryImage, setBatteryImage] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [useSampleCsv, setUseSampleCsv] = useState(true);
  const { selectedConfig, setSelectedConfig } = useBatteryConfig();
  const navigate = useNavigate();
  const batteryPicsRef = useRef(null);
  useEffect(() => {
    if (useSampleCsv) {
      fetch(SampleDataBatteryPack)
        .then((response) => response.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            const text = event.target.result;
            setFormData((prevData) => ({
              ...prevData,
              uploadData: new File([text], "sampleDataBatteryPack.csv", { type: "text/csv" }),
            }));
          };
          reader.readAsText(blob);
        });
    }else{
      setFormData((prevData) => ({
        ...prevData,
        uploadData: null,
      }));
    }
  }, [useSampleCsv]);

  // Set default battery image
  useEffect(() => {
    const setDefaultBatteryImage = async () => {
      try {
        const response = await fetch(BatteryDefaultImage);
        const blob = await response.blob();
        const defaultFile = new File([blob], "default-battery.png", { type: "image/png" });
        
        setFormData((prevData) => ({
          ...prevData,
          batteryPics: defaultFile,
        }));
        setBatteryImage(URL.createObjectURL(defaultFile));
      } catch (error) {
        console.error("Error setting default battery image:", error);
      }
    };

    setDefaultBatteryImage();
  }, []);

  useEffect(() => {
    if(Object.keys(errors).length > 0){
      for(const error in errors){
        if(errors[error]){
          setSnackbarMessage(errors[error]);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    }
  }, [errors]);

  useEffect(() => {
    if (selectedConfig) {
      setFormData({
        ...selectedConfig,
        batteryPics: selectedConfig.batteryPics
          ? URL.createObjectURL(selectedConfig.batteryPics)
          : null,
        uploadData: selectedConfig.uploadData
          ? URL.createObjectURL(selectedConfig.uploadData)
          : null,
      });
      setSubmitted(true);
    }
  }, [selectedConfig]);

  const validateForm = async () => {
    const newErrors = {};
    if (formData.batteryPics && !allowedImageTypes.includes(formData.batteryPics.type)) {
      newErrors.batteryPics = "Invalid file type. Only JPG and PNG are allowed.";
    }
    if (formData.uploadData && !allowedCsvTypes.includes(formData.uploadData.type)) {
      newErrors.uploadData = "Invalid file type. Only CSV files are allowed.";
    }
    if (formData.voltage <= 0 || formData.voltage > 1000) {
      newErrors.voltage = "Voltage must be between 1 and 1000.";
    }
    if (formData.capacity <= 0 || formData.capacity > 1000) {
      newErrors.capacity = "Capacity must be between 1 and 1000.";
    }
    if (formData.ageMonths < 0 || formData.ageMonths > 11) {
      newErrors.ageMonths = "Age months must be between 0 and 11.";
    }
    if (formData.ageYears < 0) {
      newErrors.ageYears = "Age years cannot be negative.";
    }
    if (!formData.makeModel) newErrors.makeModel = "Make/Model is required.";
    if (!formData.chemistry) newErrors.chemistry = "Chemistry is required.";
    if (!formData.format) newErrors.format = "Format is required.";
    if (!formData.uploadData) newErrors.uploadData = "CSV data upload is required.";
    if (formData.uploadData) {
      await validateCSVData(formData.uploadData).then((isValid) => {
        if (!isValid) {
          newErrors.uploadData = "Invalid CSV file. Please upload a valid CSV file.";
        }
      }).catch((error) => {
        newErrors.uploadData = error.message;
      });
    }
    // Battery picture is now optional since we set a default

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUnitChange = (event) => {
    setFormData((prevData) => ({
      ...prevData,
      capacityUnit: event.target.value,
    }));
  };

  const goToVisualize = () => {
    navigate("/batteryscope/visualize");
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      if (name === "batteryPics") {
        const file = files[0];
        if (allowedImageTypes.includes(file.type)) {
          setBatteryImage(URL.createObjectURL(file));
          setFormData((prevData) => ({
            ...prevData,
            [name]: file,
          }));
          setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "Invalid file type. Only JPG and PNG are allowed.",
          }));
        }
      } else if (name === "uploadData") {
        const file = files[0];
        if (allowedCsvTypes.includes(file.type)) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const text = event.target.result;
            const rows = text.split("\n").map((row) => row.split(","));
            setCsvData(rows);
          };
          reader.readAsText(file);
          setFormData((prevData) => ({
            ...prevData,
            [name]: file,
          }));
          setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "Invalid file type. Only CSV files are allowed.",
          }));
        }
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (await validateForm()) {
      setLoading(true);
      let updatedFormData = { ...formData };
      if (formData.capacityUnit === "kWh") {
        updatedFormData.capacity = ((formData.capacity * 1000) / (formData.voltage)).toFixed(2);
        updatedFormData.capacityUnit = "Ah";
        setFormData(updatedFormData);
      }
      const formDataToSend = new FormData();
      Object.keys(updatedFormData).forEach((key) => {
        if (updatedFormData[key] instanceof File) {
          formDataToSend.append(key, updatedFormData[key]);
        } else {
          formDataToSend.append(key, updatedFormData[key]);
        }
      });
      try {
        const newConfig = { ...updatedFormData };
        setSelectedConfig(newConfig);
        setSnackbarMessage("Data submitted successfully!");
        setSnackbarSeverity("success");
        setSubmitted(true);
      } catch (error) {
        setSnackbarMessage("Failed to submit data. Please try again.");
        setSnackbarSeverity("error");
        setSubmitted(false);
        console.error("Error:", error);
      } finally {
        setLoading(false);
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarMessage("Please fix the errors before submitting.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleRetry = () => {
    setSubmitted(false);
    handleSubmit();
  };

  const handleReset = () => {
    let csv = formData.uploadData;
    if(!useSampleCsv){
      csv = null;
    }
    setFormData({
      batteryPics: null,
      voltage: 30,
      capacity: 10,
      capacityUnit: "Ah",
      ageYears: 0,
      ageMonths: 0,
      chemistry: "lpf",
      format: "cylindrical",
      makeModel: "Samsung 18650",
      uploadData: csv,
    });
    setBatteryImage(null);
    setCsvData([]);
    setErrors({});
    setSnackbarOpen(false);
    setSubmitted(false);
    setSelectedConfig(null);
  };

  const handleEdit = () => {
    setSubmitted(false);
    setSnackbarOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <StyledBox component="form" noValidate autoComplete="off">
      <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
        {submitted ? "Submitted Battery Data" : "Submit Battery Data"}
      </StyledTypography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <label htmlFor="voltage" className="text-lg font-semibold mb-2 text-white-translucent">
            Battery Voltage
          </label>
          <StyledTextField
            id="voltage"
            name="voltage"
            type="number"
            value={formData.voltage}
            onChange={handleChange}
            fullWidth
            required
            disabled={submitted}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <StyledTypographyBlack sx={{ fontWeight: "700" }}>V</StyledTypographyBlack>
                </InputAdornment>
              ),
              inputProps: {
                min: 1,
                max: 1000,
                style: {
                  fontWeight: "700",
                  fontFamily: "Bai Jamjuree",
                },
              },
            }}
            error={!!errors.voltage}
            helpertext={errors.voltage}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <label htmlFor="capacity" className="text-lg font-semibold mb-2 text-white-translucent">
            Battery Capacity
          </label>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={9}>
              <StyledTextField
                id="capacity"
                name="capacity"
                type="text"
                value={formData.capacity}
                onChange={handleChange}
                fullWidth
                required
                disabled={submitted}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <StyledTypographyBlack sx={{ fontWeight: "700" }}>
                        {formData.capacityUnit}
                      </StyledTypographyBlack>
                    </InputAdornment>
                  ),
                  inputProps: {
                    min: 1,
                    max: 1000,
                    style: {
                      fontWeight: "700",
                      fontFamily: "Bai Jamjuree",
                    },
                  },
                }}
                error={!!errors.capacity}
                helpertext={errors.capacity}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StyledFormControl fullWidth variant="outlined">
                <StyledSelect
                  id="capacity-unit"
                  name="capacityUnit"
                  value={formData.capacityUnit}
                  onChange={handleUnitChange}
                  disabled={submitted}
                  label="Unit"
                >
                  <StyledMenuItem value="Ah">Ah</StyledMenuItem>
                  <StyledMenuItem value="kWh">kWh</StyledMenuItem>
                </StyledSelect>
              </StyledFormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6}>
          <label className="text-lg font-semibold text-white-translucent">Battery Age (Optional)</label>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                id="age-years"
                name="ageYears"
                type="number"
                value={formData.ageYears}
                onChange={handleChange}
                fullWidth
                disabled={submitted}
                InputProps={{
                  inputProps: { min: 0 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <StyledTypographyBlack sx={{ fontWeight: "700" }}>
                        {formData.ageYears > 1 ? "Years" : "Year"}
                      </StyledTypographyBlack>
                    </InputAdornment>
                  ),
                  style: {
                    fontWeight: "700",
                    fontFamily: "Bai Jamjuree",
                  },
                }}
                error={!!errors.ageYears}
                helpertext={errors.ageYears}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                id="age-months"
                name="ageMonths"
                type="number"
                value={formData.ageMonths}
                onChange={handleChange}
                fullWidth
                disabled={submitted}
                InputProps={{
                  inputProps: { min: 0, max: 11 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <StyledTypographyBlack sx={{ fontWeight: "700" }}>
                        {formData.ageMonths > 1 ? "Months" : "Month"}
                      </StyledTypographyBlack>
                    </InputAdornment>
                  ),
                  style: {
                    fontWeight: "700",
                    fontFamily: "Bai Jamjuree",
                  },
                }}
                error={!!errors.ageMonths}
                helpertext={errors.ageMonths}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6}>
          <label htmlFor="make-model" className="text-lg font-semibold text-white-translucent">
            Battery Make/Model
          </label>
          <StyledTextField
            id="make-model"
            name="makeModel"
            value={formData.makeModel}
            onChange={handleChange}
            fullWidth
            disabled={submitted}
            InputProps={{
              style: {
                fontWeight: "700",
                fontFamily: "Bai Jamjuree",
              },
            }}
            error={!!errors.makeModel}
            helpertext={errors.makeModel}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <StyledFormControl fullWidth>
            <label htmlFor="chemistry" className="text-lg font-semibold text-white-translucent">
              Battery Chemistry
            </label>
            <StyledSelect
              id="chemistry"
              name="chemistry"
              value={formData.chemistry}
              onChange={handleChange}
              disabled={submitted}
              error={!!errors.chemistry}
              helpertext={errors.chemistry}
            >
              <StyledMenuItem value="lpf">Lithium Phosphate (LPF)</StyledMenuItem>
              <StyledMenuItem value="nmc">Nickel Manganese Cobalt (NMC)</StyledMenuItem>
              <StyledMenuItem value="lto">Lithium Titanate (LTO)</StyledMenuItem>
            </StyledSelect>
          </StyledFormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <StyledFormControl fullWidth>
            <label htmlFor="format" className="text-lg font-semibold text-white-translucent">
              Cell Format
            </label>
            <StyledSelect
              id="format"
              name="format"
              value={formData.format}
              onChange={handleChange}
              disabled={submitted}
              error={!!errors.format}
              helpertext={errors.format}
            >
              <StyledMenuItem value="cylindrical">Cylindrical</StyledMenuItem>
              <StyledMenuItem value="prismatic">Prismatic</StyledMenuItem>
              <StyledMenuItem value="pouch">Pouch</StyledMenuItem>
            </StyledSelect>
          </StyledFormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <label htmlFor="cellVoltage" className="text-lg font-semibold mb-2 text-white-translucent">
            Cell Voltage
          </label>
          <StyledTextField
            id="cellVoltage"
            name="cellVoltage"
            type="number"
            value={formData.cellVoltage}
            onChange={handleChange}
            fullWidth
            required
            disabled={submitted}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <StyledTypographyBlack sx={{ fontWeight: "700" }}>V</StyledTypographyBlack>
                </InputAdornment>
              ),
              inputProps: {
                min: 1,
                max: 1000,
                style: {
                  fontWeight: "700",
                  fontFamily: "Bai Jamjuree",
                },
              },
            }}
            error={!!errors.cellVoltage}
            helpertext={errors.cellVoltage}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <label htmlFor="cellCapacity" className="text-lg font-semibold mb-2 text-white-translucent">
            Cell Capacity
          </label>

          <StyledTextField
            id="cellCapacity"
            name="cellCapacity"
            type="text"
            value={formData.cellCapacity}
            onChange={handleChange}
            fullWidth
            required
            disabled={submitted}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <StyledTypographyBlack sx={{ fontWeight: "700" }}>
                    Ah
                  </StyledTypographyBlack>
                </InputAdornment>
              ),
              inputProps: {
                min: 1,
                max: 1000,
                style: {
                  fontWeight: "700",
                  fontFamily: "Bai Jamjuree",
                },
              },
            }}
            error={!!errors.cellCapacity}
            helpertext={errors.cellCapacity}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <label htmlFor="batteryPics" className="text-lg font-semibold mb-2 text-white-translucent">
            {submitted ? "Battery Picture" : "Upload Battery Picture"}
          </label>
          {submitted ? (
            <img
              src={batteryImage || formData.batteryPics || BatteryDefaultImage}
              alt="Battery"
              style={{
                width: "50px",
                height: "55px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "5px",
                objectFit: "contain",
              }}
            />
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={10.5}>
                  <StyledTextField
                    id="batteryPics"
                    name="batteryPics"
                    ref={batteryPicsRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleChange}
                    fullWidth
                    disabled={submitted}
                    error={!!errors.batteryPics}
                    helpertext={errors.batteryPics}
                  />
                </Grid>
                <Grid item xs={1.5}>
                  <img
                    src={batteryImage || BatteryDefaultImage}
                    alt="Battery"
                    style={{
                      width: "50px",
                      height: "55px",
                      border: "1px solid #ff851b",
                      borderRadius: "4px",
                      padding: "5px",
                      objectFit: "contain",
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <label htmlFor="uploadData" className="text-lg font-semibold mb-2 text-white-translucent">
            {submitted ? "CSV Data" : "Upload CSV Data"}
          </label>
          {submitted ? (
            <div className="flex items-center gap-2 border-[1px] border-[#ccc] p-3 rounded-md bg-white">
              <a
                href={formData.uploadData}
                download
                className="flex items-center gap-2 text-lg font-semibold text-white-translucent hover:text-blue-800 transition-colors duration-300"
              >
                <AiOutlineDownload className="text-xl text-white-translucent" />
                Download CSV Data
              </a>
            </div>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StyledFormControl fullWidth variant="outlined">
                  <StyledSelect
                    value={useSampleCsv ? "sample" : "upload"}
                    onChange={(e) => setUseSampleCsv(e.target.value === "sample")}
                  >
                    <StyledMenuItem value="upload">Upload CSV</StyledMenuItem>
                    <StyledMenuItem value="sample">Use Sample CSV</StyledMenuItem>
                  </StyledSelect>
                </StyledFormControl>
              </Grid>

              {!useSampleCsv ? (
                <Grid item xs={6}>
                  <StyledTextField
                    id="uploadData"
                    name="uploadData"
                    type="file"
                    accept=".csv"
                    onChange={handleChange}
                    fullWidth
                    disabled={submitted}
                    error={!!errors.uploadData}
                    helpertext={errors.uploadData}
                  />
                </Grid>
              ) : (
                  <a
                    href={SampleDataBatteryPack}
                    download="sampleDataBatteryPack.csv"
                    className="flex items-center gap-2 text-lg font-semibold text-white-translucent hover:text-blue-800 transition-colors duration-300"
                  >
                    <AiOutlineDownload className="text-xl text-white-translucent" />
                    Download Sample CSV Data
                  </a>
              )}
            </Grid>


          )}
        </Grid>
      </Grid>
      <div className="flex justify-between gap-6">
        <StyledButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || submitted}

        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Data"}
        </StyledButton>

        {submitted && (
          <>
            <StyledButton variant="contained" onClick={handleReset} >
              Reset Form
            </StyledButton>
            <StyledButton variant="contained" onClick={handleEdit} >
              Edit Data
            </StyledButton>
            <StyledButton variant="contained" onClick={goToVisualize} className='gap-2' >
              Next Step  <ArrowForwardIcon />
            </StyledButton>
          </>
        )}
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%", fontFamily: "Bai Jamjuree" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
};

export default SubmitBatteryData;
